import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  providersApiService,
  ProviderCreateDTO,
  ProviderUpdateDTO,
  ProviderFilters,
} from '../api/providers-api';
import { Provider } from '../types/provider.types';

interface ProvidersState {
  providers: Provider[];
  currentProvider: Provider | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: ProviderFilters;
}

const initialState: ProvidersState = {
  providers: [],
  currentProvider: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchProviders = createAsyncThunk(
  'providers/fetchProviders',
  async (filters: ProviderFilters = {}, { rejectWithValue }) => {
    try {
      const response = await providersApiService.getProviders(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar proveedores'
      );
    }
  }
);

export const fetchProvider = createAsyncThunk(
  'providers/fetchProvider',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await providersApiService.getProviderById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar proveedor'
      );
    }
  }
);

export const createProvider = createAsyncThunk(
  'providers/createProvider',
  async (data: ProviderCreateDTO, { rejectWithValue }) => {
    try {
      const response = await providersApiService.createProvider(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear proveedor'
      );
    }
  }
);

export const updateProvider = createAsyncThunk(
  'providers/updateProvider',
  async ({ id, data }: { id: string; data: ProviderUpdateDTO }, { rejectWithValue }) => {
    try {
      const response = await providersApiService.updateProvider(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar proveedor'
      );
    }
  }
);

export const deleteProvider = createAsyncThunk(
  'providers/deleteProvider',
  async (id: string, { rejectWithValue }) => {
    try {
      await providersApiService.deleteProvider(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar proveedor'
      );
    }
  }
);

const providersSlice = createSlice({
  name: 'providers',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ProviderFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
    clearCurrentProvider: (state) => {
      state.currentProvider = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.providers = action.payload.providers;
        state.pagination = {
          page: action.payload.page,
          limit: action.payload.limit,
          total: action.payload.total,
          totalPages: action.payload.totalPages,
        };
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch one
      .addCase(fetchProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProvider = action.payload;
      })
      .addCase(fetchProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.providers.unshift(action.payload);
      })
      .addCase(createProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.providers.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.providers[index] = action.payload;
        }
        if (state.currentProvider?.id === action.payload.id) {
          state.currentProvider = action.payload;
        }
      })
      .addCase(updateProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.providers = state.providers.filter((p) => p.id !== action.payload);
        if (state.currentProvider?.id === action.payload) {
          state.currentProvider = null;
        }
      })
      .addCase(deleteProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, setPagination, clearCurrentProvider, clearError } = providersSlice.actions;

// Selectors
export const selectProviders = (state: any) => state.providers.providers;
export const selectCurrentProvider = (state: any) => state.providers.currentProvider;
export const selectProvidersLoading = (state: any) => state.providers.loading;
export const selectProvidersError = (state: any) => state.providers.error;
export const selectProvidersPagination = (state: any) => state.providers.pagination;
export const selectProvidersFilters = (state: any) => state.providers.filters;

export default providersSlice.reducer;

